import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaFileContract, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaCopyright, 
  FaGavel, 
  FaEnvelope,
  FaArrowLeft,
  FaCalendarAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const sections = [
    {
      icon: <FaFileContract className="w-6 h-6" />,
      title: "Acceptance of Terms",
      content: "By accessing and using Shyee Codes' services, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our services."
    },
    {
      icon: <FaCheckCircle className="w-6 h-6" />,
      title: "Services Provided",
      content: "Shyee Codes provides digital marketing services including but not limited to web development, SEO services, social media marketing, email marketing, and ready-made digital systems."
    },
    {
      icon: <FaCopyright className="w-6 h-6" />,
      title: "Intellectual Property",
      content: "All content, designs, code, and materials created by Shyee Codes remain our intellectual property until full payment is received, at which point ownership transfers to the client as specified in project agreements."
    },
    {
      icon: <FaExclamationTriangle className="w-6 h-6" />,
      title: "Limitation of Liability",
      content: "Shyee Codes shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services."
    },
    {
      icon: <FaGavel className="w-6 h-6" />,
      title: "Governing Law",
      content: "These terms shall be governed by and construed in accordance with the laws of Kenya. Any disputes shall be subject to the exclusive jurisdiction of the courts of Kenya."
    }
  ];

  const lastUpdated = "January 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c]">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(253,170,1,0.08)_0%,transparent_50%)]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <Link 
              to="/"
              className="inline-flex items-center text-white/70 hover:text-[#fdaa01] transition-colors duration-300 mb-8"
            >
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
            
            <div className="w-20 h-20 rounded-3xl bg-[#fdaa01]/10 border border-[#fdaa01]/30 flex items-center justify-center mx-auto mb-6">
              <FaFileContract className="text-4xl text-[#fdaa01]" />
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">
              Terms of <span className="text-[#fdaa01]">Service</span>
            </h1>
            
            <p className="text-white/70 text-lg mb-4">
              Last Updated: {lastUpdated}
            </p>
            
            <p className="text-white/60 max-w-2xl mx-auto leading-relaxed">
              Please read these terms carefully before using our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-xl"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#fdaa01]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#fdaa01]">{section.icon}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#010536] mb-3">
                      {index + 1}. {section.title}
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 sm:p-12 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              Need Clarification?
            </h2>
            <p className="text-white/70 mb-8">
              Contact us if you have any questions about our terms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:shyeecodes@gmail.com"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <FaEnvelope className="mr-2" />
                Contact Us
              </a>
              <a
                href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-all duration-300"
              >
                <FaCalendarAlt className="mr-2" />
                Schedule a Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;